import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, isUndefined } from 'lodash';
import moment, { MomentInput } from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BalanceSheetAllow,
  FinancialStatementFullRead,
  FinancialStatementFullWrite,
  FinancialStatementSmall as FinancialStatementVersion,
  IncomeStatementAllow,
  SpreadsheetNote,
} from 'api';
import {
  BALANCE_SHEET_NAME,
  BALANCE_SHEET_PROP,
  INCOME_STATEMENT_PROP,
  INCOME_STATEMENT_SHEET_NAME,
} from 'common/constants/financials';
import { useStore } from 'common/store';
import { FinancialStatementPeriod } from 'common/types/financials';
import { UseStoreValues } from 'common/types/store';
import { NoteData } from 'components/Notes/types';
import { IMeasurementDate } from 'models/interfaces/measurement-date.interface';
import { useVersionsFilter } from 'pages/Financials/utilities/index';
import loadFinancialStatementData from 'pages/Financials/utilities/loadFinancialStatementData';
import { useDocuments, useReadFieldAttributes } from 'services/hooks';
import { useGetCompanyFiscalYearData } from 'services/hooks/company';
import { useGetCompanyMeasurementDateByMD } from 'services/hooks/companyMeasurementDates';
import {
  useCreateFinancialStatementVersion,
  useListByMeasurementPeriod,
  useRetrieveFinancialStatement,
} from 'services/hooks/financialStatement';
import { useGetMeasurementDatesByCompanyId } from 'services/hooks/firm';
import {
  BALANCE_SHEET_MODEL_NAME,
  FINANCIALS_PAGE,
  INCOME_STATEMENT_MODEL_NAME,
  KPI_DATA_MODEL_NAME,
} from 'services/hooks/useReadFieldAttributes/constants';
import { dbShortDate, getYearFromIsoFormattedDate } from 'utilities';
import { FilesParams, NotesParams } from './types';
import { AttributeType, useRetrieveAttributeMapper } from '../../../services/hooks/attributeMapper';
import SheetAttributeManager from '../../../services/sheet-attribute-manager/SheetAttributeManager';
import { useAttributeMapperStore } from '../../../store/useAttributeMapperStore';
import { FiscalYearData } from '../performance-metrics/utilities/types';

// this should load data necessary to render the financials tables, but does not directly relate to
// specific UI surrounding the page
const useLoadFinancials = ({
  selectedMeasurementDate,
  filesToSave,
  setFilesToSave,
  notes,
  saveNotes,
  notesHasChanged,
  spreadsheetNotes,
  saveSpreadsheetNotes,
  spreadsheetNoteHasChanged,
}: {
  selectedMeasurementDate: IMeasurementDate;
  filesToSave: File[];
  setFilesToSave: (files: File[]) => void;
  notes: NoteData[] | null;
  saveNotes: () => void;
  notesHasChanged: boolean;
  spreadsheetNotes: SpreadsheetNote[];
  saveSpreadsheetNotes: () => void;
  spreadsheetNoteHasChanged: boolean;
}) => {
  const [retrieve] = useRetrieveFinancialStatement();
  const [getCompanyMeasurementDate] = useGetCompanyMeasurementDateByMD();
  const [getFinancialsVersions] = useListByMeasurementPeriod();
  const [retrieveAttributeMapper] = useRetrieveAttributeMapper();
  const { setAttributeMapperManager, clearAttributeMapperManager } = useAttributeMapperStore();

  const [measurementDates, getMeasurementDates] = useGetMeasurementDatesByCompanyId();
  const getFiscalYearData = useGetCompanyFiscalYearData();
  const [fiscalYearData, setFiscalYearData] = useState<FiscalYearData>();
  const [createdVersion, createVersion] = useCreateFinancialStatementVersion();
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const { addReferenceForExistingDocuments } = useDocuments();
  const fieldAttributesMap = useReadFieldAttributes(FINANCIALS_PAGE);
  const [financialStatement, setFinancialStatement] = useState<FinancialStatementFullWrite>();
  const [financialVersions, setFinancialVersions] = useState<FinancialStatementVersion[]>([]);
  const [companyMeasurementDate, setCompanyMeasurementDate] = useState<IMeasurementDate>();
  const [periods, setPeriods] = useState<FinancialStatementPeriod[] | undefined>(undefined);
  const [incomeStatement, setIncomeStatement] = useState({});
  const [balanceSheets, setBalanceSheets] = useState({});
  const [kpi, setKpi] = useState({});
  const [additionalHistoricalYears, setAdditionalHistoricalYears] = useState<FinancialStatementPeriod[]>();
  const [showAdditionalHistoricalYears, setShowAdditionalHistoricalYears] = useState(false);
  const measurementDateRef = useRef<IMeasurementDate | null>(null);
  const financialStatementId = financialStatement?.id;

  const fieldAttributes = useMemo(
    () => ({
      incomeStatementAttrs: fieldAttributesMap.get(INCOME_STATEMENT_MODEL_NAME),
      balanceSheetAttrs: fieldAttributesMap.get(BALANCE_SHEET_MODEL_NAME),
      kpiAttrs: fieldAttributesMap.get(KPI_DATA_MODEL_NAME),
    }),
    [fieldAttributesMap]
  );

  const { selectedVersion } = useVersionsFilter({
    financialVersions,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const getVersions = useCallback(async () => {
    try {
      const tmpVersions = await getFinancialsVersions(selectedMeasurementDate?.id as number);
      setFinancialVersions(tmpVersions);
    } catch (error) {
      throw new Error(error as string);
    }
  }, [getFinancialsVersions, selectedMeasurementDate]);

  useEffect(() => {
    if (companyInfo?.id) {
      getMeasurementDates(companyInfo.id);
    }
  }, [companyInfo?.id, getMeasurementDates]);

  useEffect(
    () => () => {
      setPeriods(undefined);
    },
    [selectedVersion, companyInfo?.id]
  );

  useEffect(() => {
    const loadMDBasedData = async () => {
      if (companyMeasurementDate?.id !== selectedMeasurementDate?.cmd_id || isUndefined(companyMeasurementDate?.id)) {
        const cmdPromise = getCompanyMeasurementDate(selectedMeasurementDate?.id) as Promise<IMeasurementDate>;
        const fyDataPromise = getFiscalYearData(selectedMeasurementDate?.cmd_id) as Promise<FiscalYearData>;
        const tmpVersionsPromise = getFinancialsVersions(selectedMeasurementDate?.id as number);
        try {
          const [cmd, fyData, tmpVersions] = await Promise.all([cmdPromise, fyDataPromise, tmpVersionsPromise]);

          measurementDateRef.current = selectedMeasurementDate;
          setCompanyMeasurementDate(cmd);
          setFiscalYearData(fyData);
          setFinancialVersions(tmpVersions);

          if (isEmpty(tmpVersions)) {
            setFinancialStatement({
              financial_statement_periods: [],
              company_measurement_date: cmd.id,
            } as unknown as FinancialStatementFullWrite);
          } else {
            setFinancialStatement(undefined);
          }
        } catch (error) {
          throw new Error(error as string);
        }
      }
    };

    if (selectedMeasurementDate?.id) {
      loadMDBasedData();
    }
  }, [
    companyMeasurementDate,
    companyInfo?.id,
    getCompanyMeasurementDate,
    getFinancialsVersions,
    getFiscalYearData,
    selectedMeasurementDate,
  ]);

  useEffect(() => {
    if (selectedVersion?.id) {
      const fetchStatement = async () => {
        try {
          const tmpFinancialStatement = (await retrieve(selectedVersion.id)) as FinancialStatementVersion;

          if (tmpFinancialStatement.sheet_attribute_mapping) {
            const retrievedMapper = await retrieveAttributeMapper(
              tmpFinancialStatement.sheet_attribute_mapping,
              AttributeType.FINANCIALS
            );

            const mapping = new SheetAttributeManager(retrievedMapper)


            setAttributeMapperManager(mapping);
          } else {
            clearAttributeMapperManager();
          }
          setFinancialStatement(tmpFinancialStatement as unknown as FinancialStatementFullWrite);
        } catch (error) {
          throw new Error(error as string);
        }
      };
      fetchStatement();
    }
  }, [retrieve, selectedVersion, retrieveAttributeMapper, setAttributeMapperManager, clearAttributeMapperManager]);

  useEffect(() => {
    if (financialStatement) {
      setShowAdditionalHistoricalYears(financialStatement.show_additional_historical_years || false);
    }
  }, [financialStatement, setShowAdditionalHistoricalYears]);

  const currentFiscalYearEnd = useMemo(() => {
    if (selectedMeasurementDate && fiscalYearData) {
      const md = selectedMeasurementDate;

      const measurementDate = md?.date;
      const currentPeriod = moment(measurementDate);
      const currentFiscalYear = moment(measurementDate);

      currentFiscalYear.set({
        month: fiscalYearData.fiscal_year_end!.end_month! - 1,
        date: fiscalYearData.fiscal_year_end!.end_day,
      });
      if (currentPeriod.isAfter(currentFiscalYear)) {
        currentFiscalYear.add(1, 'years');
      }
      return currentFiscalYear;
    }
  }, [fiscalYearData, selectedMeasurementDate]);

  // update the additional historical years
  useEffect(() => {
    if (
      financialStatement?.financial_statement_periods &&
      selectedMeasurementDate?.id &&
      selectedMeasurementDate?.cmd_id === financialStatement?.company_measurement_date &&
      fiscalYearData &&
      currentFiscalYearEnd
    ) {
      const historicalYearsProps = {
        setAdditionalHistoricalYears,
        showAdditionalHistoricalYears: financialStatement.show_additional_historical_years || false,
      };

      loadFinancialStatementData(
        selectedMeasurementDate,
        financialStatement,
        fiscalYearData,
        setPeriods,
        historicalYearsProps,
        currentFiscalYearEnd
      );
    }
  }, [financialStatement, fiscalYearData, selectedMeasurementDate, setPeriods, currentFiscalYearEnd]);

  useEffect(() => {
    if (createdVersion) {
      getVersions();

      const searchParams = new URLSearchParams(location.search);
      searchParams.set('version', (createdVersion as FinancialStatementFullRead)?.slug as string);
      const newSearch = searchParams.toString();

      navigate({
        pathname: location.pathname,
        search: `?${newSearch}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdVersion, getVersions, history, location.pathname]);

  const changePeriodDate = useCallback(
    (periodId: number, value: MomentInput, tableName: string, sameTableColumns: FinancialStatementPeriod[]) => {
      const periodToChangeIndex = (sameTableColumns || []).findIndex(({ id }) => id === periodId);
      const propToAccess = {
        [INCOME_STATEMENT_SHEET_NAME]: INCOME_STATEMENT_PROP,
        [BALANCE_SHEET_NAME]: BALANCE_SHEET_PROP,
      };
      const propAccessed = propToAccess[tableName as keyof typeof propToAccess];

      if (sameTableColumns && periodToChangeIndex) {
        const newDate = dbShortDate(value);
        const newYear = getYearFromIsoFormattedDate(newDate);
        /* account for periods not having balance_sheet and income_statement
        if they're not persisted yet */
        const auxColumns = [...sameTableColumns];
        const periodToChange = auxColumns[periodToChangeIndex];
        periodToChange.year = newYear;
        const specificData = periodToChange[propAccessed as keyof FinancialStatementPeriod] as
          | IncomeStatementAllow
          | BalanceSheetAllow;
        specificData.reported_statement_date = newDate;
        return auxColumns;
      }

      return [];
    },
    []
  );

  const filesParams: FilesParams = useMemo(
    () => ({
      addReferenceForExistingDocuments,
      filesToSave,
      financialStatementId,
      setFilesToSave,
    }),
    [addReferenceForExistingDocuments, filesToSave, financialStatementId, setFilesToSave]
  );

  const notesParams: NotesParams = useMemo(
    () => ({
      notes,
      notesHasChanged,
      saveNotes,
      spreadsheetNotes,
      saveSpreadsheetNotes,
      spreadsheetNoteHasChanged,
    }),
    [notes, notesHasChanged, saveNotes, saveSpreadsheetNotes, spreadsheetNoteHasChanged, spreadsheetNotes]
  );

  return {
    periods,
    changePeriodDate,
    financialStatement,
    setFinancialStatement,
    incomeStatement,
    setIncomeStatement,
    balanceSheets,
    setBalanceSheets,
    kpi,
    setKpi,
    selectedVersion,
    filesParams,
    notesParams,
    companyMeasurementDate,
    fieldAttributes,
    measurementDates,
    createdVersion,
    createVersion,
    fiscalYearData,
    setPeriods,
    setShowAdditionalHistoricalYears,
    additionalHistoricalYears,
    setAdditionalHistoricalYears,
    showAdditionalHistoricalYears,
    getVersions,
    currentFiscalYearEnd,
  };
};

export default useLoadFinancials;
